import { CommonModule } from '@angular/common';
import { Component, effect, inject, input } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LegalPersonValidatedFormComponent } from '@app/components/legal-person-validated-form/legal-person-validated-form.component';
import { getLegalEntitiyData } from '@app/components/legal-person-validated-form/legal-person-validated-form.helper';
import type {
  LegalEntityFormGroup,
  LegalEntityFormValue,
} from '@app/models/form.models';
import type { LegalEntityRequest } from '@app/models/project-setup.models';
import type { LegalEntityDetails } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ToastService } from '@app/services/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';

@Component({
  selector: 'app-details-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    LegalPersonValidatedFormComponent,
  ],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss',
})
export class DetailsTabComponent {
  readonly entity = input<LegalEntityDetails | null>(null);

  private readonly fb = inject(FormBuilder);
  private readonly service = inject(LegalEntityService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  legalPersonForm: FormGroup<LegalEntityFormGroup> = getLegalEntitiyData(
    this.fb
  );
  private initialValue: LegalEntityFormValue | null = null;

  constructor() {
    effect(() => {
      const entity = this.entity();
      if (!entity) return;

      const formValue = this.toFormValue(entity);
      this.initialValue = formValue;
      this.legalPersonForm.reset(formValue, { emitEvent: false });
      this.legalPersonForm.markAsPristine();
      this.legalPersonForm.markAsUntouched();
    });
  }

  private toFormValue(entity: LegalEntityDetails): LegalEntityFormValue {
    return {
      taxNumber: entity.taxNumber ?? '',
      name: entity.name ?? '',
      email: entity.email ?? '',
      country: entity.country ?? '',
      ownershipStructure: entity.ownershipStructure ?? null,
      contactInfo: entity.contactInfo ?? '',
      website: entity.website ?? '',
      teaorNumbers: entity.teaorNumbers ?? null,
      bankAccountNumber: entity.bankAccountNumber ?? '',
      companyTypeId: entity.companyType?.id ?? '',
      address: {
        countryCode: entity.address?.countryCode ?? '',
        zipCode: entity.address?.zipCode ?? '',
        city: entity.address?.city ?? '',
        publicSpace: entity.address?.publicSpace ?? '',
        publicSpaceType: entity.address?.publicSpaceType ?? '',
        houseNumber: entity.address?.houseNumber ?? '',
        doorOrFloor: entity.address?.doorOrFloor ?? '',
      },
    };
  }

  save(): Observable<void> | null {
    const id = this.entity()?.id;
    if (!id) return null;

    this.legalPersonForm.updateValueAndValidity({ emitEvent: false });
    if (this.legalPersonForm.invalid) {
      this.legalPersonForm.markAllAsTouched();
      return null;
    }

    const legalPerson = this.legalPersonForm.getRawValue();
    const payload: LegalEntityRequest = {
      ownershipStructure: legalPerson.ownershipStructure ?? 0,
      contactInfo: legalPerson.contactInfo ?? '',
      website: legalPerson.website ?? '',
      teaorNumbers: legalPerson.teaorNumbers ?? null,
      companyTypeId: legalPerson.companyTypeId ?? '',
      taxNumber: legalPerson.taxNumber,
      name: legalPerson.name,
      email: legalPerson.email,
      country: legalPerson.country,
      bankAccountNumber: legalPerson.bankAccountNumber ?? '',
      address: {
        countryCode: legalPerson.address.countryCode,
        zipCode: legalPerson.address.zipCode,
        city: legalPerson.address.city,
        publicSpace: legalPerson.address.publicSpace,
        publicSpaceType: legalPerson.address.publicSpaceType,
        houseNumber: legalPerson.address.houseNumber,
        doorOrFloor: legalPerson.address.doorOrFloor,
      },
    };
    return this.service.updateLegalEntity(id, payload).pipe(
      tap(() =>
        this.toast.success(
          this.translate.instant('admin.toasts.savedSuccessfully')
        )
      ),
      map(() => void 0),
      catchError((err) => {
        this.toast.error(this.translate.instant('admin.toasts.saveFailed'));
        return throwError(() => err);
      })
    );
  }

  cancel(): void {
    this.legalPersonForm.reset(this.initialValue ?? undefined, {
      emitEvent: false,
    });
    this.legalPersonForm.markAsPristine();
    this.legalPersonForm.markAsUntouched();
  }
}
